import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

const languages = [
  { code: "en", label: "English", path: "/en/" },
  { code: "fr", label: "Français", path: "/fr/" },
  { code: "da", label: "Dansk", path: "/da/" },
  { code: "de", label: "Deutsch", path: "/de/" },
];

const categories = [
  { key: "shoes", labelKey: "categories.shoes", url: "https://kaspreadsheet.com/category-911-Shoes.html" },
  { key: "hoodies", labelKey: "categories.hoodies", url: "https://kaspreadsheet.com/search-912/name/Shoes.html" },
  { key: "tshirts", labelKey: "categories.tshirts", url: "https://kaspreadsheet.com/search-913/name/Shoes.html" },
  { key: "jackets", labelKey: "categories.jackets", url: "https://kaspreadsheet.com/search-914/name/Shoes.html" },
  { key: "pants", labelKey: "categories.pants", url: "https://kaspreadsheet.com/search-915/name/Shoes.html" },
  { key: "headwear", labelKey: "categories.headwear", url: "https://kaspreadsheet.com/search-916/name/Shoes.html" },
  { key: "sets", labelKey: "categories.sets", url: "https://kaspreadsheet.com/search-917/name/Shoes.html" },
  { key: "underwear", labelKey: "categories.underwear", url: "https://kaspreadsheet.com/search-918/name/Shoes.html" },
  { key: "jersey", labelKey: "categories.jersey", url: "https://kaspreadsheet.com/search-919/name/Shoes.html" },
  { key: "accessories", labelKey: "categories.accessories", url: "https://kaspreadsheet.com/search-920/name/Shoes.html" },
  { key: "bags", labelKey: "categories.bags", url: "https://kaspreadsheet.com/search-921/name/Shoes.html" },
  { key: "belts", labelKey: "categories.belts", url: "https://kaspreadsheet.com/search-922/name/Shoes.html" },
  { key: "perfumes", labelKey: "categories.perfumes", url: "https://kaspreadsheet.com/search-923/name/Shoes.html" },
  { key: "other", labelKey: "categories.other", url: "https://kaspreadsheet.com/search-924/name/Shoes.html" },
];

export default function Header() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);

  const currentLang = languages.find((l) => location.pathname.startsWith(l.path)) || languages[0];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) setCatOpen(false);
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const switchLang = (lang: typeof languages[0]) => {
    const newPath = location.pathname.replace(/^\/(en|fr|da|de)\//, lang.path);
    i18n.changeLanguage(lang.code);
    navigate(newPath || lang.path);
    setLangOpen(false);
  };

  const getLangPath = (path: string) => {
    const lang = currentLang.code;
    return `/${lang}${path}`;
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href={getLangPath("/")} className="flex items-center gap-2 group">
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-primary-500 flex items-center justify-center">
              <span className="text-background-50 font-heading font-bold text-sm md:text-base">A</span>
            </div>
            <span className={`font-heading font-semibold text-base md:text-lg transition-colors ${scrolled ? "text-foreground-950" : "text-background-50"}`}>
              AllChinaBuy
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            <a href={getLangPath("/")} className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${scrolled ? "text-foreground-700 hover:text-foreground-950 hover:bg-background-100" : "text-background-50/90 hover:text-background-50 hover:bg-background-50/10"}`}>
              {t("nav.home")}
            </a>
            <a href={getLangPath("/allchinabuy-spreadsheet/")} className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${scrolled ? "text-foreground-700 hover:text-foreground-950 hover:bg-background-100" : "text-background-50/90 hover:text-background-50 hover:bg-background-50/10"}`}>
              {t("nav.spreadsheet")}
            </a>

            {/* Categories dropdown */}
            <div className="relative" ref={catRef}>
              <button
                onClick={() => setCatOpen(!catOpen)}
                className={`px-3 py-2 rounded-md text-sm font-medium flex items-center gap-1 transition-colors ${scrolled ? "text-foreground-700 hover:text-foreground-950 hover:bg-background-100" : "text-background-50/90 hover:text-background-50 hover:bg-background-50/10"}`}
              >
                {t("nav.categories")}
                <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? "rotate-180" : ""}`} />
              </button>
              {catOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 rounded-xl shadow-lg border border-background-200/70 overflow-hidden py-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.key}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors"
                    >
                      {t(cat.labelKey)}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${scrolled ? "text-foreground-700 hover:text-foreground-950 hover:bg-background-100" : "text-background-50/90 hover:text-background-50 hover:bg-background-50/10"}`}>
              {t("nav.guides")}
            </a>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${scrolled ? "text-foreground-700 hover:text-foreground-950 hover:bg-background-100" : "text-background-50/90 hover:text-background-50 hover:bg-background-50/10"}`}>
              {t("nav.safety")}
            </a>
          </nav>

          {/* Right side */}
          <div className="hidden lg:flex items-center gap-2">
            {/* Language switcher */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className={`px-3 py-2 rounded-md text-sm font-medium flex items-center gap-1 transition-colors ${scrolled ? "text-foreground-700 hover:text-foreground-950 hover:bg-background-100" : "text-background-50/90 hover:text-background-50 hover:bg-background-50/10"}`}
              >
                <i className="ri-global-line" />
                {currentLang.label}
                <i className={`ri-arrow-down-s-line transition-transform ${langOpen ? "rotate-180" : ""}`} />
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-2 w-40 bg-background-50 rounded-xl shadow-lg border border-background-200/70 overflow-hidden py-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => switchLang(lang)}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                        lang.code === currentLang.code
                          ? "text-primary-600 bg-primary-50 font-medium"
                          : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t("nav.cta")}
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`lg:hidden p-2 rounded-md transition-colors ${scrolled ? "text-foreground-950 hover:bg-background-100" : "text-background-50 hover:bg-background-50/10"}`}
          >
            <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-xl`} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200/70 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <a href={getLangPath("/")} className="block px-3 py-2.5 rounded-lg text-foreground-700 hover:bg-background-100 text-sm font-medium">
              {t("nav.home")}
            </a>
            <a href={getLangPath("/allchinabuy-spreadsheet/")} className="block px-3 py-2.5 rounded-lg text-foreground-700 hover:bg-background-100 text-sm font-medium">
              {t("nav.spreadsheet")}
            </a>
            <div className="px-3 py-2 text-xs font-medium text-foreground-400 uppercase tracking-wider">
              {t("nav.categories")}
            </div>
            {categories.map((cat) => (
              <a
                key={cat.key}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="block px-3 py-2 rounded-lg text-foreground-600 hover:bg-background-100 text-sm"
              >
                {t(cat.labelKey)}
              </a>
            ))}
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="block px-3 py-2.5 rounded-lg text-foreground-700 hover:bg-background-100 text-sm font-medium">
              {t("nav.guides")}
            </a>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="block px-3 py-2.5 rounded-lg text-foreground-700 hover:bg-background-100 text-sm font-medium">
              {t("nav.safety")}
            </a>

            <div className="pt-3 border-t border-background-200/70">
              <div className="px-3 py-2 text-xs font-medium text-foreground-400 uppercase tracking-wider">
                {t("nav.language")}
              </div>
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    switchLang(lang);
                    setMobileOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    lang.code === currentLang.code
                      ? "text-primary-600 bg-primary-50 font-medium"
                      : "text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="block mt-3 px-4 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-medium text-center hover:bg-primary-600 transition-colors"
            >
              {t("nav.cta")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}